/**
 * Phase 41 (Plan 41-03 Task 1) — Notifications API client.
 *
 * Wraps the Phase 39 backend surface:
 *   GET    /api/notifications?unread=1&kind=...&limit=...
 *   PATCH  /api/notifications/:id              (mark single read)
 *   POST   /api/notifications/mark-all-read    (bulk mark)
 *
 * The list response envelope is `{ data: Notification[], total: number,
 * unreadCount: number }` — every key explicitly enumerated in the backend
 * Fastify response schema (`backend/src/api/schemas/notification.ts:62-70`).
 * Pitfall 3 lock: fast-json-stringify silently drops top-level keys not
 * listed under `properties` AND `required`, so the contract is verified at
 * both ends. We mirror the FE-visible shape here (NB: backend omits
 * `recipientId` from notification rows — implicit currentUser, T-39-04-01
 * mitigation; we drop it from the FE type as well).
 */
import { apiGet, apiPatch, apiPost } from './client';

export interface Notification {
  id: number;
  kind:
    | 'sla.warning'
    | 'sla.breach'
    | 'package.threshold_crossed'
    | 'package.cap_blocked'
    | 'package.zero_rate_overage'
    | 'package.overage_threshold_crossed'
    | 'package.overage_approved'
    | 'training.due'
    | 'training.overdue'
    // Quick 260806-ddg — Linear-style default task notifications.
    | 'task.assigned'
    | 'task.activity'
    // Phase 87 — Claude bot usage weekly-window threshold alerts. Admin-only,
    // and projectless: the payload carries a bot connection + user, no project.
    | 'bot_usage.threshold_crossed'
    // Quick task 260914-pxp — a project's PM is notified when a human
    // creates an internal task in a project they do not manage.
    | 'task.created'
    // MKS-209 — assigned to a project for the month but logged nothing on it.
    // Slack is the default channel; this in-app lane exists for admins who
    // route the kind to in_app in the notification matrix.
    | 'nothing_logged'
    // MKS-139 — the daily billing-schedule sweep. Unlike MKS-265's
    // backdated-hours kinds, in_app here is a first-class seeded lane, not a
    // no-Slack-handle fallback: these rows land on this page for every
    // recipient, so they must be in this union or they render as their raw
    // code (the Phase 87 bug recorded above `bot_usage.threshold_crossed`).
    | 'billing_period.ending_soon'
    | 'billing_period.phase_overdue'
    | 'billing_period.ready_not_invoiced'
    | 'invoice.overdue';
  payload: Record<string, unknown>;
  stateChangeId: number;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationListResponse {
  data: Notification[];
  total: number;
  unreadCount: number;
}

export interface NotificationListParams {
  /**
   * `true`  → unread rows only (readAt IS NULL)
   * `false` → solved rows only (readAt IS NOT NULL) — UAT 2026-05-13
   * `undefined` → no filter
   */
  unread?: boolean;
  kind?: string;
  limit?: number;
}

export function fetchNotifications(params?: NotificationListParams): Promise<NotificationListResponse> {
  const qs = new URLSearchParams();
  // Backend accepts `unread=true|false` per notificationListQuery.enum, NOT '1'.
  if (params?.unread === true) qs.set('unread', 'true');
  else if (params?.unread === false) qs.set('unread', 'false');
  if (params?.kind) qs.set('kind', params.kind);
  if (params?.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiGet<NotificationListResponse>(`/notifications${suffix}`);
}

export function markNotificationRead(id: number): Promise<{ data: { id: number; readAt: string | null } }> {
  return apiPatch<{ data: { id: number; readAt: string | null } }>(`/notifications/${id}`, {});
}

export function markAllNotificationsRead(): Promise<{ data: { updated: number } }> {
  return apiPost<{ data: { updated: number } }>(`/notifications/mark-all-read`, {});
}
